import { AHSeparator, AHWidgetTitle, TouchableBox } from "arkh";
import React, { useCallback, useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";

import { graphql, useFragment } from "react-relay";

import { useSetWidgetFocus } from "../useSetFocus";
import { FlatList, ListRenderItem, Platform } from "react-native";

import { WidgetIssueSummaryTeamItem } from "./WidgetIssueSummaryTeamItem";
import { WidgetIssueSummary_widgetIssueSummary$key } from "./__generated__/WidgetIssueSummary_widgetIssueSummary.graphql";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetIssueSummaryIssueChangedSubscription } from "./__generated__/WidgetIssueSummaryIssueChangedSubscription.graphql";
import { WidgetIssueSummaryIssueCreatedSubscription } from "./__generated__/WidgetIssueSummaryIssueCreatedSubscription.graphql";

export const conf = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

export const WidgetIssueSummary = (
  props: {
    widget: WidgetIssueSummary_widgetIssueSummary$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetIssueSummary_widgetIssueSummary on WidgetIssueSummary {
        id
        title
        teams {
          id
          unresolvedIssueCount
          ...WidgetIssueSummaryTeamItem_team
        }
      }
    `,
    props.widget
  );
  const wrapperStyle = useMemo(() => ({ flex: 1 }), []);

  const teamIds = useMemo(
    () => widget?.teams?.map((t) => t.id) ?? [],
    [widget.teams]
  );
  useSubscriptionWithSkip<WidgetIssueSummaryIssueCreatedSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription WidgetIssueSummaryIssueCreatedSubscription(
            $teamIds: [ID!]!
          ) {
            issueCreated(teamIds: $teamIds) {
              store {
                teams(ids: $teamIds) {
                  unresolvedIssueCount
                }
              }
            }
          }
        `,
        variables: { teamIds },
      }),
      [teamIds]
    )
  );
  useSubscriptionWithSkip<WidgetIssueSummaryIssueChangedSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription WidgetIssueSummaryIssueChangedSubscription(
            $teamIds: [ID!]!
          ) {
            issueStatusChanged(teamIds: $teamIds) {
              store {
                teams(ids: $teamIds) {
                  unresolvedIssueCount
                }
              }
            }
          }
        `,
        variables: { teamIds },
      }),
      [teamIds]
    )
  );
  const { onEvent } = props;

  const focus = useSetWidgetFocus(widget.id, props.storecastId);
  const onPressItem = useCallback(
    (id: string, teamName: string, teamColor: string | null) => {
      if (props.mode === "FOCUS") {
        focus(id);
      } else {
        onEvent?.({
          kind: "AllIssues",
          teamId: id,
        });
      }
    },
    [focus, onEvent, props.mode]
  );

  const onBackgroundClick = useCallback(() => {
    if (props.mode === "FOCUS") {
      focus();
    }
  }, [focus, props.mode]);

  const renderItem: ListRenderItem<(typeof widget.teams)[number]> = useCallback(
    ({ item }) => {
      return <WidgetIssueSummaryTeamItem onPress={onPressItem} team={item} />;
    },
    [onPressItem]
  );

  const sortedTeams = useMemo(
    () =>
      widget.teams
        .slice()
        .sort((a, b) => b.unresolvedIssueCount - a.unresolvedIssueCount),
    [widget.teams]
  );
  return (
    <TouchableBox
      disabled={Platform.OS !== "web" && props.mode !== "FOCUS"}
      onPress={onBackgroundClick}
      style={wrapperStyle}
      color="surface"
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        data={sortedTeams}
        renderItem={renderItem}
        scrollEnabled={props.interactive !== false}
        showsVerticalScrollIndicator={Platform.OS !== "web"}
      />
    </TouchableBox>
  );
};
